import React, { useEffect, useState, useRef } from "react";
import { connect, useDispatch } from "react-redux";
import { format } from "date-fns";
import * as Yup from "yup";
import { bool, func, shape, string } from "prop-types";
import { Field, Form, Formik } from "formik";
import * as i18nUtils from "util/i18n";
import { actions as formActions, selectors as formSelectors } from "reducers/form";
import CustomText from "pages/_components/CustomText/CustomText";
import Button from "pages/_components/Button";
import { formatNum } from "util/format";
import Credential from "pages/_components/fields/credentials/Credential";
import TextField from "pages/_components/fields/TextField";

const CancelTransactionForm = ({ handleClose, cleanForm, transaction: propsTransaction, isCancellingTransaction }) => {
    const dispatch = useDispatch();
    const [trxData, setTrxData] = useState({});
    const formRef = useRef(null);
    const [shouldCloseForm, setShouldCloseForm] = useState(false);

    const resetForm = () => {
        formRef.current.setFieldValue("password", "");
        formRef.current.setFieldValue("transactionName", "");
        formRef.current.setFieldValue("creationDate", "");
        formRef.current.setFieldValue("submitDate", "");
        formRef.current.setFieldValue("amount", "");
    }

    const reset = () => {
        resetForm();
        setShouldCloseForm(false);
    }

    useEffect(() => {
        if (cleanForm) {
            formRef.current.setFieldValue("password", "");
        }
    }, [cleanForm]);

    useEffect(() => {
        if (!isCancellingTransaction && shouldCloseForm) {
            handleClose();
            reset();
        }
    }, [isCancellingTransaction]);

    useEffect(() => {
        if (propsTransaction && Object.keys(propsTransaction).length > 0) {
            const { transaction, transactionAmounts } = propsTransaction;

            const {
                idTransaction,
                creationDateTime,
                submitDateTime,
                activityName,
                idTransactionStatus,
                environmentName,
                environmentType,
            } = transaction;

            let [currency] = Object.keys(transactionAmounts);
            let amount = transactionAmounts[currency];

            let activityTitle = activityName;

            if (activityName === i18nUtils.get("activities.debin.create.send")) {
                amount = transaction?.data?.amountField?.amount;
                currency = transaction?.data?.amountField?.currency;
            }
            if (transaction.data.routeParam === "afip") {
                activityTitle = i18nUtils.get("payments.service.afip");
            }
            if (!amount || !currency) {
                amount = transaction?.data?.amount?.quantity;
                currency = transaction?.data?.amount?.currency;
            }

            const showAmount = !!amount && !!currency;

            setTrxData({
                idTransaction,
                creationDateTime,
                submitDateTime,
                activityTitle,
                idTransactionStatus,
                environmentName,
                environmentType,
                amount,
                currency,
                showAmount,
            });

        }
    }, [propsTransaction]);

    useEffect(() => {
        if (trxData && formRef && formRef.current) {
            const dateFormatDefault = i18nUtils.get("datepicker.format");
            const creationDate = format(trxData?.creationDateTime, dateFormatDefault);
            const submitDate = format(trxData?.submitDateTime, dateFormatDefault);

            formRef.current.setFieldValue("transactionName", trxData?.activityTitle && trxData?.activityTitle.includes(" ") ? trxData?.activityTitle : i18nUtils.get(trxData?.activityTitle, "-"));
            formRef.current.setFieldValue("creationDate", creationDate);
            formRef.current.setFieldValue("submitDate", submitDate);

            const currency = i18nUtils.get(`currency.label.${trxData?.currency}`);
            formRef.current.setFieldValue("amount", `${currency} ${formatNum(trxData?.amount, 2)}`);
        }
    }, [trxData]);

    const formikProps = {
        validateOnChange: true,
        validateOnBlur: false,
        initialValues: {
            password: "",
            termAndConditions: false,
        },
        validationSchema: (props) =>
            Yup.object().shape({
                password: Yup.string().required(i18nUtils.get("deposits.request.required"))
            }),
        onSubmit: (values, formikBag) => {
            const { password } = values;
            const formikBagParam = {
                ...formikBag,
                props: {
                    idTransaction: propsTransaction?.transaction?.idTransaction,
                    hideNotificationError: true,
                    changeScopeNotification: true,
                }
            }
            dispatch(formActions.cancelTransaction({ credentials: { password, otp: "" }, formikBag: formikBagParam }));
            setShouldCloseForm(true);
        }
    }

    return (
        <div className="container centered">
            <h1 className="title-content form-drawer-title">
                <CustomText text={i18nUtils.get("menu.cancel.transaction", "Cancelar transacción")} />
            </h1>
            <Formik
                {...formikProps}
                ref={formRef}
            >
                {() => (
                    <Form style={{ width: "80%" }}>
                        <div>
                            <div className="pb-6" />
                            <Field
                                autoComplete="off"
                                component={TextField}
                                maxLength={50}
                                name="status"
                                labelText="transactions.list.header.status"
                                value={i18nUtils.get("transaction.status.PENDING")}
                                hidePlaceholder
                                disabled
                            />
                            <Field
                                autoComplete="off"
                                component={TextField}
                                maxLength={50}
                                name="transactionName"
                                labelText="transactions.list.header.transactionName"
                                hidePlaceholder
                                disabled
                            />
                            <Field
                                autoComplete="off"
                                component={TextField}
                                maxLength={50}
                                name="creationDate"
                                labelText="transactions.list.header.creationDate"
                                hidePlaceholder
                                disabled
                            />
                            <Field
                                autoComplete="off"
                                component={TextField}
                                maxLength={50}
                                name="submitDate"
                                labelText="transactions.list.header.submitDate"
                                hidePlaceholder
                                disabled
                            />
                            {trxData?.showAmount &&
                                < Field
                                    autoComplete="off"
                                    component={TextField}
                                    maxLength={50}
                                    name="amount"
                                    labelText="transactions.list.header.amount"
                                    hidePlaceholder
                                    disabled
                                />}
                            <Field
                                name="password"
                                labelText="form.credential.password.label"
                                placeholderText="Ej: Ju@n_123"
                                component={Credential}
                                type="password"
                            />
                            <Button
                                block
                                type="submit"
                                label="global.cancel"
                                bsStyle="primary"
                                loading={false}
                            />
                        </div>
                    </Form>
                )}
            </Formik>
        </div >
    );
};

CancelTransactionForm.propTypes = {
    isDesktop: bool.isRequired,
    fetching: bool.isRequired,
    handleClose: func.isRequired,
    cleanForm: bool.isRequired,
    transaction: shape({}).isRequired,
    isCancellingTransaction: bool.isRequired,
    fetchingCancel: bool.isRequired,
    // eslint-disable-next-line react/no-unused-prop-types
    idTransaction: string.isRequired,
};

CancelTransactionForm.defaultProps = {};

const mapStateToProps = (state) => ({
    isCancellingTransaction: formSelectors.getIsCancellingTransaction(state),
    fetchingCancel: formSelectors.getFetching(state),
});

export default connect(mapStateToProps)(CancelTransactionForm);
